'use strict';

/**
 * FavManager: A Vue-like reactive module for managing favorites.
 * Separates Data (items) from View (render).
 */
export class FavManager {
	constructor() {
		// Initial state hydration from the server-provided variable
		this.state = {
			items: this._get_initial_items(),
			labels: {}, // { msgID: { active: 'Text', inactive: 'Text' } },
			is_logged_in: !!yalwa.basics.is_logged_in(),
			use_info_api: yalwa.v.use_info_api && yalwa.v.countryID
		};
		
		this._init_labels();
		this._bind_events();
		this.render();
		this._handle_after_login_action();
	}
	
	_handle_after_login_action() {
		if (yalwa.v.after_login_fav_ad && yalwa.v.after_login_fav_ad_msgID) {
			const fav_element = jQuery('.js-fav[data-msgid="' + yalwa.v.after_login_fav_ad_msgID  + '"]');
			fav_element.trigger('click.fav')
			yalwa.v.after_login_fav_ad = false;
		}
	}
	
	/**
	 * Determines initial items: Prioritizes LocalStorage if HTML is cached,
	 * otherwise uses the fresh server variable.
	 */
	_get_initial_items() {
		if (yalwa.basics.is_html_cached()) {
			const cached = yalwa.basics.ls_get('favorites');
			return cached ? JSON.parse(cached) : (yalwa.v.favorites || []);
		}
		// Fresh render: yalwa.v.favorites is the absolute source of truth
		const items = yalwa.v.favorites || [];
		yalwa.basics.ls_set('favorites', JSON.stringify(items));
		return items;
	}
	
	/**
	 * Hydrates the label map. This solves the "unknown toggle text" issue
	 */
	_init_labels() {
		const self = this;
		jQuery('.js-fav').each(function() {
			const $el = jQuery(this);
			const id = $el.data('msgid');
			const $label = $el.find('.js-list_label');
			
			if ($label.length && id) {
				const save_label = $label.text().trim();
				const saved_label = $el.data('toggle_text');
				// If active on load, currentText is the 'active' label.
				self.state.labels[id] = {
					saved: saved_label,
					save: save_label,
				};
			}
		});
	}
	
	_bind_events() {
		// Global Delegation: No need to re-init after AJAX content loads
		jQuery('body').on('click.fav', '.js-fav', (event) => {
			// Pass both the DOM element (this) and the Event object (event)
			this.handle_click(event.currentTarget, event);
		});
		
		// BF-Cache / Visibility Handler
		window.addEventListener('pageshow', (e) => this._handle_pageshow(e));
	}
	
	/**
	 * The Reactive 'Render' function.
	 * Whenever data changes, we call this to sync the entire DOM.
	 */
	render() {
		const self = this;
		jQuery('.js-fav').each(function() {
			const $el = jQuery(this);
			const id = $el.data('msgid');
			const is_active = self.state.items.includes(id);
			
			// Update Icons
			const $icon = $el.find('.yicon, .yimage');
			$icon.toggleClass('star--inactive inactive', !is_active);
			
			// Update Labels
			if (self.state.labels[id]) {
				const $label = $el.find('.js-list_label');
				const target_label = is_active ? self.state.labels[id].saved : self.state.labels[id].save;
				if ($label.text() !== target_label) {
					$label.text(target_label);
				}
			}
		});
		
		// Update Global Counter/Visuals
		const has_favs = this.state.items.length > 0;
		jQuery('.js-ybtb_fav .yif').toggleClass('yif_star_color', has_favs);
	}
	/**
	 * Resets all favorites.
	 * Clears local state, informs server, and reloads the page.
	 */
	async reset() {
		// Handle UI side effects (scroll position)
		if (typeof yalwa.mobile.mainscroll_set === 'function') {
			yalwa.mobile.mainscroll_set(0);
		}
		yalwa.basics.state.extend({scroll_position: yalwa.mobile.mainscroll_get()}, true);
		
		try {
			let response;
			// Perform API call based on country/config
			if (yalwa.v.use_info_api && yalwa.v.countryID) {
				response = await yalwa.basics.ajaxservice({
					type: 'POST',
					resource: 'favorites',
					action: 'clear',
					is_cross_origin: true,
					include_session: true
				});
			} else {
				response = await jQuery.post('/api/favorites/clear/', {});
			}
			
			// Clear Local State
			this.state.items = [];
			yalwa.basics.ls_set('favorites', JSON.stringify([]));
			
			// Final action
			if (response && (response.success || !yalwa.v.use_info_api)) {
				yalwa.basics.reload();
			}
		} catch (err) {
			console.error('Fav reset failed', err);
		}
	}
	
	/**
	 * Entry point for interactions.
	 * Handles the auth logic using the preserved event context.
	 */
	handle_click(element, event) {
		if (!yalwa.basics.readonly_check()) {
			return;
		}
		
		if (!yalwa.basics.is_logged_in()) {
			return yalwa.mobile.auth.show_dialog(event);
		}
		this.toggle(element);
	}
	
	async toggle(element) {
		const msgID = yalwa.basics.get_msgID(element);
		
		try {
			const response = await this._perform_toggle_request(msgID);
			if (response && response.success) {
				const is_added = response.data[0] === 1;
				this._update_state(msgID, is_added);
				
				if (response.nudge) {
					this._trigger_nudge(response);
				}
			}
		} catch (err) {
			console.error('Fav toggle failed', err);
		}
	}
	
	async _perform_toggle_request(msgID) {
		if (this.state.use_info_api) {
			return yalwa.basics.ajaxservice({
				type: 'POST', resource: 'favorites', action: 'toggle',
				identifier: msgID, is_cross_origin: true, include_session: true
			});
		}
		return jQuery.post('/api/favorites/toggle/', { msgID });
	}
	
	_update_state(msgID, is_added) {
		if (is_added) {
			if (!this.state.items.includes(msgID)) this.state.items.push(msgID);
		} else {
			this.state.items = this.state.items.filter(id => id !== msgID);
		}
		
		yalwa.basics.ls_set('favorites', JSON.stringify(this.state.items));
		this.render();
	}
	
	_handle_pageshow(event) {
		// Sync login state
		const login_changed = (!!yalwa.basics.get_cookie('LoggedIn') !== this.state.is_logged_in);
		
		if (event.persisted || yalwa.basics.is_back_forward_navigation(event)) {
			if (login_changed) {
				window.location.reload();
				return;
			}
			// Refresh state from disk and re-render
			this.state.items = JSON.parse(yalwa.basics.ls_get('favorites')) || [];
			this.render();
		}
	}
	
	_trigger_nudge(res) {
		const $container = jQuery('.js-plp_container');
		if (res.nudge_plp) {
			$container.append(res.nudge_plp).addClass('plp_container--center');
			jQuery('.js-plp_nudge_chat').trigger('plp_show');
			yalwa.mobile.init_nudge();
		}
	}
}